import { useRef, useState, useEffect } from "react";

/**
 * EditableField — wraps ANY resume field with inline editing support.
 *
 * When editMode=true:
 *   - field turns into a contentEditable span
 *   - green underline indicates it's editable
 *   - onBlur saves via the onSave callback
 *   - Escape key cancels the edit
 *
 * When editMode=false: renders as normal (no interactivity).
 *
 * Usage:
 *   <EditableField editMode={editMode} value={name} onSave={(v) => onFieldUpdate("name", v)} tag="h1" className="..." />
 */
export default function EditableField({
  value,
  onSave,
  editMode,
  tag: Tag = "span",
  className = "",
  style = {},
  multiline = false,
  placeholder = "Click to edit",
  ...rest
}) {
  const ref = useRef(null);
  const [original, setOriginal] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  // Keep DOM in sync when external value changes (e.g. AI updates)
  useEffect(() => {
    if (ref.current && !isFocused) {
      if (ref.current.innerText !== value) {
        ref.current.innerText = value || "";
      }
      setOriginal(value);
    }
    // When focused, NEVER update innerText or original — preserves user's edit
    // and keeps the blur comparison accurate.
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    setOriginal(ref.current?.innerText || "");
  };

  const handleBlur = () => {
    setIsFocused(false);
    const newVal = ref.current?.innerText?.trim() || "";
    if (newVal !== original.trim()) {
      onSave(newVal);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      // Cancel — restore original
      if (ref.current) ref.current.innerText = original;
      ref.current?.blur();
    }
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      ref.current?.blur(); // save on Enter for single-line fields
    }
  };

  if (!editMode) {
    return (
      <Tag className={className} style={style} {...rest}>
        {value}
      </Tag>
    );
  }

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
      data-placeholder={placeholder}
      style={{
        ...style,
        outline: "none",
        borderBottom: isFocused
          ? "2px solid #22c55e"
          : "2px dashed rgba(34,197,94,0.5)",
        borderRadius: "1px",
        cursor: "text",
        minWidth: "40px",
        transition: "border-color 0.15s",
      }}
      title="Click to edit"
      {...rest}
    >
      {value}
    </Tag>
  );
}
